/**
 * Tickets, Nachrichten, Tags und Snippets (Spec 3.2 / 3.3 / 8.2).
 *
 * Diese Datei ist die Single Source of Truth fuer API, Admin-UI und (ab Phase 3)
 * das SDK. Kein Paket definiert eigene Ticket-Schemas.
 */
import { z } from 'zod';
import { paginationQuerySchema } from '../envelope.js';
import { attachmentSchema } from './attachment.js';
import { emailSchema, localeSchema, uuidSchema } from './common.js';
import { contactSchema } from './contact.js';
import { tiptapDocSchema } from './richtext.js';
export const TICKET_STATUSES = [
    'open',
    'pending',
    'waiting_customer',
    'resolved',
    'closed',
];
export const ticketStatusSchema = z.enum(TICKET_STATUSES);
export const TICKET_PRIORITIES = ['normal', 'important', 'urgent'];
export const ticketPrioritySchema = z.enum(TICKET_PRIORITIES);
export const MESSAGE_KINDS = ['inbound', 'outbound', 'internal_note'];
export const messageKindSchema = z.enum(MESSAGE_KINDS);
export const MESSAGE_VIA = ['api', 'email', 'ui'];
export const messageViaSchema = z.enum(MESSAGE_VIA);
/** Zustellstatus ausgehender Mails (ADR 0002) — gefuellt ab Phase 2. */
export const MESSAGE_DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'bounced'];
export const messageDeliveryStatusSchema = z.enum(MESSAGE_DELIVERY_STATUSES);
/** Ticketnummer je Mandant fortlaufend: `T-<Jahr>-<4-stellig>` (Spec 3.2). */
export const TICKET_NUMBER_PATTERN = /^T-\d{4}-\d{4,}$/;
export const ticketNumberSchema = z.string().regex(TICKET_NUMBER_PATTERN);
// ---------------------------------------------------------------- Tags
export const tagSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    name: z.string().min(1).max(60),
    /** Hex-Farbe fuer die Anzeige im UI. */
    color: z.string().regex(/^#[0-9a-f]{6}$/i, 'Farbe als Hex-Wert, z. B. #3b82f6.'),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createTagSchema = tagSchema.pick({ name: true, color: true });
export const updateTagSchema = createTagSchema.partial();
// ------------------------------------------------------------ Snippets
export const SNIPPET_SCOPES = ['tenant', 'queue', 'personal'];
export const snippetScopeSchema = z.enum(SNIPPET_SCOPES);
export const snippetSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    title: z.string().min(1).max(200),
    body_json: tiptapDocSchema,
    scope: snippetScopeSchema,
    /** Nur bei `scope: 'queue'` gesetzt. */
    queue_id: uuidSchema.nullable(),
    /** Nur bei `scope: 'personal'` gesetzt — der Server setzt es aus der Session. */
    owner_member_id: uuidSchema.nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createSnippetSchema = z
    .object({
    title: z.string().min(1).max(200),
    body_json: tiptapDocSchema,
    scope: snippetScopeSchema,
    queue_id: uuidSchema.nullable().default(null),
})
    .refine((input) => (input.scope === 'queue') === (input.queue_id !== null), {
    message: 'Nur Queue-Snippets referenzieren eine Queue — und sie muessen es.',
    path: ['queue_id'],
});
export const updateSnippetSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    body_json: tiptapDocSchema.optional(),
});
// ------------------------------------------------------------ Messages
export const ticketMessageSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    kind: messageKindSchema,
    author_member_id: uuidSchema.nullable(),
    contact_id: uuidSchema.nullable(),
    via: messageViaSchema,
    body_json: tiptapDocSchema,
    body_text: z.string(),
    /** Serverseitig gerendert und sanitisiert — nie der Rohtext des Clients. */
    body_html: z.string(),
    email_message_id: z.string().nullable(),
    /** Aus welchem KI-Entwurf die Nachricht entstanden ist (Spec 3.2, Phase 4). */
    ai_draft_id: uuidSchema.nullable(),
    /** Bearbeiter-Name aus der Quell-App (ADR 0018) — nur bei Ingest-Antworten. */
    external_author_name: z.string().nullable(),
    delivery_status: messageDeliveryStatusSchema.nullable(),
    delivery_error: z.string().nullable(),
    provider_message_id: z.string().nullable(),
    attempts: z.number().int().min(0),
    attachments: z.array(attachmentSchema),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/**
 * Was das Admin-UI anlegen darf. `inbound` fehlt bewusst: eingehende
 * Nachrichten entstehen ausschliesslich in der Mail-Pipeline (Phase 2) bzw.
 * ueber die Ingest-API (Phase 3), nie durch einen Agent-Klick.
 *
 * `ai_draft_id` (Phase 4) ist der **einzige** Weg, auf dem aus einem
 * KI-Entwurf eine Nachricht wird (Architektur-Invariante 3): der Agent sendet
 * ihn ueber genau diesen Endpunkt ab, inklusive Session-Auth, `ticket.write`
 * und Scope-Pruefung. Der Entwurf geht dabei auf `sent`.
 */
export const createTicketMessageSchema = z.object({
    kind: z.enum(['outbound', 'internal_note']),
    body_json: tiptapDocSchema,
    attachment_ids: z.array(uuidSchema).max(20).default([]),
    /** Herkunft aus einem KI-Entwurf (Spec 3.2: `ticket_messages.ai_draft_id`). */
    ai_draft_id: uuidSchema.nullable().default(null),
});
// ------------------------------------------------------------- Tickets
export const ticketSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    queue_id: uuidSchema,
    source_id: uuidSchema.nullable(),
    contact_id: uuidSchema,
    number: ticketNumberSchema,
    subject: z.string().min(1).max(500),
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
    assignee_member_id: uuidSchema.nullable(),
    team_id: uuidSchema.nullable(),
    due_at: z.iso.datetime().nullable(),
    /** Ticket-ID im Quellsystem — Idempotenz-Schluessel der Ingest-API (Phase 3). */
    external_ref: z.string().max(200).nullable(),
    metadata: z.record(z.string(), z.unknown()),
    merged_into_ticket_id: uuidSchema.nullable(),
    /** Gesetzt, sobald eine Eskalationsregel gegriffen hat (Spec 8.4). */
    escalated_at: z.iso.datetime().nullable(),
    language: localeSchema,
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/** Ist die Frist ueberschritten? Reine Funktion — API **und** UI benutzen sie. */
export function isOverdue(ticket, now = new Date()) {
    if (ticket.due_at === null)
        return false;
    // Erledigte Tickets sind nie ueberfaellig — die Frist galt bis zur Loesung.
    if (ticket.status === 'resolved' || ticket.status === 'closed')
        return false;
    return new Date(ticket.due_at).getTime() < now.getTime();
}
/** Zeile der Ticket-Liste: das Ticket plus alles, was die Liste anzeigt. */
export const ticketListItemSchema = ticketSchema.extend({
    queue_name: z.string(),
    source_name: z.string().nullable(),
    contact_display_name: z.string(),
    tags: z.array(tagSchema),
    message_count: z.number().int().min(0),
    last_message_at: z.iso.datetime().nullable(),
});
export const ticketDetailSchema = ticketListItemSchema.extend({
    contact: contactSchema,
    messages: z.array(ticketMessageSchema),
});
/** Kontakt eines neuen Tickets: bestehender Contact **oder** neue Angaben. */
export const ticketContactInputSchema = z.object({
    display_name: z.string().min(1).max(200),
    email: emailSchema.nullable().default(null),
});
export const createTicketSchema = z
    .object({
    queue_id: uuidSchema,
    source_id: uuidSchema.nullable().default(null),
    subject: z.string().min(1).max(500),
    priority: ticketPrioritySchema.default('normal'),
    contact_id: uuidSchema.optional(),
    contact: ticketContactInputSchema.optional(),
    assignee_member_id: uuidSchema.nullable().default(null),
    team_id: uuidSchema.nullable().default(null),
    due_at: z.iso.datetime().nullable().default(null),
    tag_ids: z.array(uuidSchema).max(20).default([]),
    language: localeSchema.default('de'),
    /** Erste Nachricht. Im UI ist das die Problembeschreibung des Kunden. */
    message: z.object({
        kind: messageKindSchema.default('inbound'),
        body_json: tiptapDocSchema,
        attachment_ids: z.array(uuidSchema).max(20).default([]),
    }),
})
    .refine((input) => Boolean(input.contact_id) !== Boolean(input.contact), {
    message: 'Entweder contact_id oder contact angeben, nicht beides.',
    path: ['contact_id'],
});
export const updateTicketSchema = z.object({
    subject: z.string().min(1).max(500).optional(),
    priority: ticketPrioritySchema.optional(),
    queue_id: uuidSchema.optional(),
    assignee_member_id: uuidSchema.nullable().optional(),
    team_id: uuidSchema.nullable().optional(),
    due_at: z.iso.datetime().nullable().optional(),
});
export const updateTicketStatusSchema = z.object({ status: ticketStatusSchema });
export const updateTicketTagsSchema = z.object({ tag_ids: z.array(uuidSchema).max(20) });
/**
 * Merge zweier Tickets (Spec 7.2). Das **adressierte** Ticket ist die Quelle,
 * `target_ticket_id` das Ziel: die Quelle wird geschlossen und traegt danach
 * `merged_into_ticket_id`.
 *
 * `merge_contacts` ist die Antwort auf die Rueckfrage „Sind das dieselbe
 * Person?" aus Spec 7.2 — ohne ausdrueckliches `true` bleiben die Kontakte
 * getrennt. Kein Raten, keine Automagie.
 */
export const mergeTicketSchema = z.object({
    target_ticket_id: uuidSchema,
    merge_contacts: z.boolean().default(false),
});
/** Sortierung der Ticket-Liste (Spec 8.3: Fristen sichtbar machen). */
export const TICKET_SORTS = ['created_desc', 'due_asc', 'updated_desc'];
export const ticketSortSchema = z.enum(TICKET_SORTS);
/** `?status=open,pending` → `['open','pending']`. Leerwerte fallen raus. */
const commaSeparated = (item) => z.preprocess((value) => typeof value === 'string'
    ? value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
    : value, z.array(item));
export const ticketListQuerySchema = paginationQuerySchema.extend({
    status: commaSeparated(ticketStatusSchema).optional(),
    queue_id: uuidSchema.optional(),
    source_id: uuidSchema.optional(),
    assignee_member_id: uuidSchema.optional(),
    team_id: uuidSchema.optional(),
    tag_id: uuidSchema.optional(),
    contact_id: uuidSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    /** Volltextsuche ueber Betreff, Nachrichtentexte und Contact-Name (Spec 11). */
    q: z.string().min(1).max(200).optional(),
    /** Nur ueberfaellige Tickets (Spec 8.3) — `due_at` in der Vergangenheit. */
    overdue: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true')
        .optional(),
    sort: ticketSortSchema.default('created_desc'),
});
//# sourceMappingURL=ticket.js.map